import {
  Document,
  PDFViewer,
  Page as PDFPage,
  StyleSheet,
  Text as PDFText,
  View as PDFView,
  Image as PDFImage,
  Font
} from '@react-pdf/renderer';

Font.register({
  family: 'Roboto',
  fonts: [{ src: '/Roboto-Bold.ttf', fontWeight: 700 }]
});

// ─── Design tokens ───────────────────────────────────────────────
const C = {
  primary:      '#1565C0',
  primaryDark:  '#0D47A1',
  primaryLight: '#E3F2FD',
  sectionBg:    '#1976D2',
  subtotalBg:   '#BBDEFB',
  dateBg:       '#F5F5F5',
  altRow:       '#F5F8FF',
  white:        '#FFFFFF',
  dark:         '#1A1A2E',
  border:       '#B0BEC5',
  green:        '#1B5E20',
  red:          '#B71C1C',
  gray:         '#546E7A',
};

// ─── Column widths ───────────────────────────────────────────────
const COL = {
  sr:       '3%',
  patient:  '17%',
  uhid:     '6%',
  admNo:    '7%',
  address:  '13%',
  mobile:   '8%',
  service:  '9%',
  payMode:  '6%',
  panel:    '8%',
  billAmt:  '8%',
  disc:     '7%',
  netAmt:   '8%',
};

const styles = StyleSheet.create({
  page: { padding: '16pt 18pt', fontSize: 7, color: C.dark, backgroundColor: C.white },

  logo:        { width: '100%', height: 64, marginBottom: 2 },
  headerRule:  { height: 2, backgroundColor: C.primary, marginBottom: 4 },
  reportTitle: { fontSize: 11, fontFamily: 'Roboto', fontWeight: 700, textAlign: 'center', color: C.primaryDark, marginBottom: 1 },
  doctorTitle: { fontSize: 9, fontFamily: 'Roboto', fontWeight: 700, textAlign: 'center', color: C.primary, marginBottom: 1 },
  reportSub:   { fontSize: 7, textAlign: 'center', color: C.gray, marginBottom: 5 },

  tableHead: { flexDirection: 'row', backgroundColor: C.sectionBg, borderRadius: 2 },
  th: {
    color: C.white, fontFamily: 'Roboto', fontWeight: 700, fontSize: 6.5,
    paddingVertical: 4, paddingHorizontal: 2,
    textAlign: 'center', borderRightWidth: 0.5, borderRightColor: '#90CAF9',
  },

  sectionStrip: {
    backgroundColor: C.sectionBg, paddingVertical: 4, paddingHorizontal: 8,
    marginTop: 10, borderRadius: 2,
  },
  sectionStripText: { fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.white },

  dateBar: {
    backgroundColor: C.dateBg, borderLeftWidth: 3, borderLeftColor: C.primary,
    paddingVertical: 2, paddingHorizontal: 6, marginTop: 5, marginBottom: 0,
  },
  dateBarText: { fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark },

  serviceLabel: {
    paddingVertical: 2, paddingHorizontal: 8,
    backgroundColor: C.primaryLight, borderBottomWidth: 0.3, borderBottomColor: C.border,
  },
  serviceLabelText: { fontSize: 6.5, color: C.primary, fontStyle: 'italic' },

  row:     { flexDirection: 'row', borderBottomWidth: 0.3, borderBottomColor: C.border },
  rowEven: { backgroundColor: C.white },
  rowOdd:  { backgroundColor: C.altRow },
  td: { fontSize: 6.5, paddingVertical: 2.5, paddingHorizontal: 2, borderRightWidth: 0.3, borderRightColor: C.border },

  dailyTotalRow: {
    flexDirection: 'row', justifyContent: 'flex-end',
    backgroundColor: C.subtotalBg, borderTopWidth: 0.5, borderTopColor: C.primary,
    paddingVertical: 2, paddingHorizontal: 6, marginBottom: 4,
  },
  dailyTotalText: { fontSize: 6.5, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark },

  sectionTotalRow: {
    flexDirection: 'row',
    backgroundColor: C.subtotalBg,
    borderTopWidth: 1, borderTopColor: C.primary,
    borderBottomWidth: 0.5, borderBottomColor: C.primary,
    paddingVertical: 3, paddingHorizontal: 6, marginBottom: 6,
  },
  sectionTotalLabel: { flex: 1, fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, textAlign: 'right' },
  sectionTotalAmt:   { fontSize: 7, fontFamily: 'Roboto', fontWeight: 700, color: C.primaryDark, paddingLeft: 8 },

  grandBox: {
    marginTop: 12, borderWidth: 1.5, borderColor: C.primaryDark,
    backgroundColor: C.primaryDark, borderRadius: 4,
    flexDirection: 'row', paddingVertical: 6, paddingHorizontal: 10, alignItems: 'center',
  },
  grandLabel: { flex: 1, fontSize: 10, fontFamily: 'Roboto', fontWeight: 700, color: C.white },
  grandAmt:   { fontSize: 12, fontFamily: 'Roboto', fontWeight: 700, color: '#FFD54F' },

  footer: {
    position: 'absolute', bottom: 10, left: 18, right: 18,
    flexDirection: 'row', borderTopWidth: 0.5, borderTopColor: C.border, paddingTop: 4,
  },
  footerLeft:  { flex: 1, fontSize: 6, color: C.gray },
  footerRight: { fontSize: 6, color: C.gray, textAlign: 'right' },

  emptyText: { fontSize: 6.5, color: C.gray, fontStyle: 'italic', padding: 6 },
});

// ─── Helpers ──────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '-';
  const dt = new Date(d);
  if (isNaN(dt)) return '-';
  return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`;
}
function fmt(n)    { return (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtINR(n) { return `₹ ${fmt(n)}`; }
function nowStr()  {
  const d = new Date();
  return `${fmtDate(d)}  ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

// ─── Sub-components ───────────────────────────────────────────────
const TH = ({ w, children, align = 'center' }) => (
  <PDFText style={[styles.th, { width: w, textAlign: align }]}>{children}</PDFText>
);
const TD = ({ w, children, align = 'center', bold, color }) => (
  <PDFText style={[styles.td, { width: w, textAlign: align, color: color || C.dark },
    bold ? { fontFamily: 'Roboto', fontWeight: 700 } : {}]}>
    {children ?? ''}
  </PDFText>
);
const TableHeader = () => (
  <PDFView style={styles.tableHead}>
    <TH w={COL.sr}>#</TH>
    <TH w={COL.patient} align="left">Patient Name</TH>
    <TH w={COL.uhid}>UHID</TH>
    <TH w={COL.admNo}>Adm / OP No.</TH>
    <TH w={COL.address} align="left">Address</TH>
    <TH w={COL.mobile}>Mobile</TH>
    <TH w={COL.service} align="left">Service</TH>
    <TH w={COL.payMode}>Pay Mode</TH>
    <TH w={COL.panel} align="left">Panel</TH>
    <TH w={COL.billAmt} align="right">Bill Amt.</TH>
    <TH w={COL.disc} align="right">Disc.</TH>
    <TH w={COL.netAmt} align="right">Net Amt.</TH>
  </PDFView>
);

const groupByDate = (arr) => {
  if (!Array.isArray(arr)) return {};
  return arr.reduce((g, item) => {
    const d = fmtDate(item?.date);
    if (!g[d]) g[d] = [];
    g[d].push(item);
    return g;
  }, {});
};
const groupByService = (arr) => {
  if (!Array.isArray(arr)) return {};
  return arr.reduce((g, item) => {
    const s = item?.serviceDetail?.service_name || item?.serviceDetail?.type || 'Consultancy';
    if (!g[s]) g[s] = [];
    g[s].push(item);
    return g;
  }, {});
};

// ─── Main Component ───────────────────────────────────────────────
export const DoctorLedgerReportPdf = ({ data }) => {
  if (!data) return null;

  const ipdRows    = data?.ipdBillingData?.data    || [];
  const opdRows    = data?.opdData?.data            || [];
  const opdBillRows= data?.opdBillingData?.data     || [];

  const opdGrouped     = groupByDate(opdRows);
  const opdBillGrouped = groupByDate(opdBillRows);
  const ipdGrouped     = groupByDate(ipdRows);

  // Bill-level discount (disc_amt on the bill doc) repeats on every unwound service row —
  // count it once per bill _id. Total discount = item-level + bill-level.
  const sumBy = (grouped, getValue, getDisc, hasBillDisc) => {
    const t = { bill: 0, itemDisc: 0, billDisc: 0, disc: 0, net: 0 };
    const seen = {};
    Object.values(grouped).forEach(rows => rows.forEach(i => {
      t.bill     += parseFloat(getValue(i)) || 0;
      t.itemDisc += parseFloat(getDisc(i)) || 0;
      if (hasBillDisc && i?._id && !seen[i._id]) {
        seen[i._id] = true;
        t.billDisc += parseFloat(i?.disc_amt) || 0;
      }
    }));
    t.disc = t.itemDisc + t.billDisc;
    t.net  = t.bill - t.disc;
    return t;
  };

  const opdTotal     = sumBy(opdGrouped,     i => i?.reg,                    i => i?.disc,                    false);
  const opdBillTotal = sumBy(opdBillGrouped, i => i?.service_items?.amount, i => i?.service_items?.disc_amt, true);
  const ipdTotal     = sumBy(ipdGrouped,     i => i?.service_items?.amount, i => i?.service_items?.disc_amt, true);

  const grandBill = opdTotal.bill + opdBillTotal.bill + ipdTotal.bill;
  const grandDisc = opdTotal.disc + opdBillTotal.disc + ipdTotal.disc;
  const grandNet  = grandBill - grandDisc;

  const doctorName =
    opdRows[0]?.consultant?.name ||
    opdBillRows[0]?.patient?.consultant?.name ||
    ipdRows[0]?.consultant?.name || 'Doctor';

  const printTime = nowStr();

  const renderSection = (grouped, getValue, getDisc, getPatientName, getUhid, getAdm, getAddress, getMobile, getService, getPayMode, getPanel, hasBillDisc) => {
    if (Object.keys(grouped).length === 0)
      return <PDFView><PDFText style={styles.emptyText}>No records found.</PDFText></PDFView>;

    return Object.keys(grouped).map(groupDate => {
      const byService = groupByService(grouped[groupDate]);
      let dailyBill = 0, dailyDisc = 0;
      // Bill-level discount: counted once per bill _id within this date group
      let dailyBillDisc = 0;
      if (hasBillDisc) {
        const seen = {};
        grouped[groupDate].forEach(r => {
          if (r?._id && !seen[r._id]) { seen[r._id] = true; dailyBillDisc += parseFloat(r?.disc_amt) || 0; }
        });
      }
      return (
        <PDFView key={groupDate}>
          <PDFView style={styles.dateBar}>
            <PDFText style={styles.dateBarText}>Date: {groupDate}</PDFText>
          </PDFView>
          {Object.keys(byService).map(svcName => (
            <PDFView key={svcName}>
              <PDFView style={styles.serviceLabel}>
                <PDFText style={styles.serviceLabelText}>{svcName}</PDFText>
              </PDFView>
              {byService[svcName].map((item, idx) => {
                const amt  = parseFloat(getValue(item)) || 0;
                const disc = parseFloat(getDisc ? getDisc(item) : 0) || 0;
                const net  = amt - disc;
                dailyBill += amt;
                dailyDisc += disc;
                return (
                  <PDFView key={idx} style={[styles.row, idx % 2 === 0 ? styles.rowEven : styles.rowOdd]}>
                    <TD w={COL.sr}>{idx + 1}</TD>
                    <TD w={COL.patient} align="left">{getPatientName(item)}</TD>
                    <TD w={COL.uhid}>{getUhid(item)}</TD>
                    <TD w={COL.admNo}>{getAdm(item)}</TD>
                    <TD w={COL.address} align="left">{getAddress(item)}</TD>
                    <TD w={COL.mobile}>{getMobile(item)}</TD>
                    <TD w={COL.service} align="left">{getService(item)}</TD>
                    <TD w={COL.payMode}>{getPayMode(item)}</TD>
                    <TD w={COL.panel} align="left">{getPanel(item)}</TD>
                    <TD w={COL.billAmt} align="right">{fmt(amt)}</TD>
                    <TD w={COL.disc} align="right" color={disc > 0 ? C.red : C.dark}>{fmt(disc)}</TD>
                    <TD w={COL.netAmt} align="right" bold>{fmt(net)}</TD>
                  </PDFView>
                );
              })}
            </PDFView>
          ))}
          <PDFView style={styles.dailyTotalRow}>
            <PDFText style={styles.dailyTotalText}>
              Daily — Bill: {fmtINR(dailyBill)}
              {dailyBillDisc > 0
                ? `   Disc: ${fmtINR(dailyDisc + dailyBillDisc)} (Item ${fmtINR(dailyDisc)} + Bill ${fmtINR(dailyBillDisc)})`
                : `   Disc: ${fmtINR(dailyDisc)}`}
              {'   '}Net: {fmtINR(dailyBill - dailyDisc - dailyBillDisc)}
            </PDFText>
          </PDFView>
        </PDFView>
      );
    });
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 1000, backgroundColor: 'white' }}>
      <PDFViewer width="100%" height="100%" showToolbar>
        <Document title="Doctor Ledger Detail Report">
          <PDFPage style={styles.page} size="A4" orientation="landscape" wrap>

            {/* HEADER */}
            <PDFImage src="/logo_Hospital_m.jpeg" style={styles.logo} />
            <PDFView style={styles.headerRule} />
            <PDFText style={styles.reportTitle}>DOCTOR LEDGER — DETAILED REPORT</PDFText>
            <PDFText style={styles.doctorTitle}>{doctorName}</PDFText>
            <PDFText style={styles.reportSub}>Printed on: {printTime}</PDFText>
            <PDFView style={[styles.headerRule, { height: 0.5, marginBottom: 4 }]} />

            {/* ── OPD CONSULTANCY ─────────────────────────── */}
            {Object.keys(opdGrouped).length > 0 && (
              <PDFView>
                <PDFView style={styles.sectionStrip}>
                  <PDFText style={styles.sectionStripText}>OPD CONSULTANCY  ({opdRows.length} patients)</PDFText>
                </PDFView>
                <TableHeader />
                <PDFView>
                  {renderSection(
                    opdGrouped,
                    i => i?.reg,
                    i => i?.disc,
                    i => `${i?.name_suffix||''} ${i?.first_name||''} ${i?.last_name||''}`.trim() || '-',
                    i => i?.uhid || '-',
                    i => i?.adm_no_date || '-',
                    i => i?.address?.address || '-',
                    i => i?.address?.mobile || '-',
                    () => 'Consultancy',
                    i => i?.payment_mode || '-',
                    i => i?.sponsor?.name || '-',
                    false,
                  )}
                </PDFView>
                <PDFView style={styles.sectionTotalRow}>
                  <PDFText style={styles.sectionTotalLabel}>
                    OPD Consultancy — Bill: {fmtINR(opdTotal.bill)}   Disc: {fmtINR(opdTotal.disc)}   Net:
                  </PDFText>
                  <PDFText style={styles.sectionTotalAmt}>{fmtINR(opdTotal.net)}</PDFText>
                </PDFView>
              </PDFView>
            )}

            {/* ── OPD BILLING ─────────────────────────────── */}
            {Object.keys(opdBillGrouped).length > 0 && (
              <PDFView>
                <PDFView style={styles.sectionStrip}>
                  <PDFText style={styles.sectionStripText}>OPD BILLING  ({opdBillRows.length} bills)</PDFText>
                </PDFView>
                <TableHeader />
                <PDFView>
                  {renderSection(
                    opdBillGrouped,
                    i => i?.service_items?.amount,
                    i => i?.service_items?.disc_amt,
                    i => `${i?.patient?.name_suffix||''} ${i?.patient?.first_name||''} ${i?.patient?.last_name||''}`.trim() || '-',
                    i => i?.uhid || i?.patient?.uhid || '-',
                    i => i?.patient?.adm_no_date || '-',
                    i => i?.patient?.address?.address || '-',
                    i => i?.patient?.address?.mobile || '-',
                    i => i?.serviceDetail?.service_name || i?.serviceDetail?.type || '-',
                    i => i?.pay_mode || '-',
                    i => i?.patient?.sponsor?.name || '-',
                    true,
                  )}
                </PDFView>
                <PDFView style={styles.sectionTotalRow}>
                  <PDFText style={styles.sectionTotalLabel}>
                    OPD Billing — Bill: {fmtINR(opdBillTotal.bill)}   Disc: {fmtINR(opdBillTotal.disc)} (Item {fmtINR(opdBillTotal.itemDisc)} + Bill {fmtINR(opdBillTotal.billDisc)})   Net:
                  </PDFText>
                  <PDFText style={styles.sectionTotalAmt}>{fmtINR(opdBillTotal.net)}</PDFText>
                </PDFView>
              </PDFView>
            )}

            {/* ── IPD BILLING ─────────────────────────────── */}
            {Object.keys(ipdGrouped).length > 0 && (
              <PDFView>
                <PDFView style={styles.sectionStrip}>
                  <PDFText style={styles.sectionStripText}>IPD BILLING  ({ipdRows.length} bills)</PDFText>
                </PDFView>
                <TableHeader />
                <PDFView>
                  {renderSection(
                    ipdGrouped,
                    i => i?.service_items?.amount,
                    i => i?.service_items?.disc_amt,
                    i => `${i?.patient?.name_suffix||''} ${i?.patient?.first_name||''} ${i?.patient?.last_name||''}`.trim() || '-',
                    i => i?.patient?.uhid || '-',
                    i => i?.ipd_no || '-',
                    i => i?.patient?.address?.address || '-',
                    i => i?.patient?.address?.mobile || '-',
                    i => i?.serviceDetail?.service_name || i?.serviceDetail?.type || '-',
                    i => i?.pay_mode || '-',
                    i => i?.patient?.sponsor?.name || '-',
                    true,
                  )}
                </PDFView>
                <PDFView style={styles.sectionTotalRow}>
                  <PDFText style={styles.sectionTotalLabel}>
                    IPD Billing — Bill: {fmtINR(ipdTotal.bill)}   Disc: {fmtINR(ipdTotal.disc)} (Item {fmtINR(ipdTotal.itemDisc)} + Bill {fmtINR(ipdTotal.billDisc)})   Net:
                  </PDFText>
                  <PDFText style={styles.sectionTotalAmt}>{fmtINR(ipdTotal.net)}</PDFText>
                </PDFView>
              </PDFView>
            )}

            {/* No data fallback */}
            {Object.keys(opdGrouped).length === 0 && Object.keys(opdBillGrouped).length === 0 && Object.keys(ipdGrouped).length === 0 && (
              <PDFView style={{ padding: 20, borderWidth: 1, borderColor: C.border, marginTop: 10 }}>
                <PDFText style={{ fontSize: 9, color: C.gray, textAlign: 'center' }}>
                  No records found for the selected doctor and date range.
                </PDFText>
              </PDFView>
            )}

            {/* FINAL CALCULATION SUMMARY */}
            <PDFView style={{ marginTop: 12, borderWidth: 1.5, borderColor: C.primaryDark, borderRadius: 4 }}>
              <PDFView style={{ flexDirection: 'row', backgroundColor: C.primaryDark, paddingVertical: 4, paddingHorizontal: 8 }}>
                <PDFText style={{ flex: 2, fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.white }}>FINAL CALCULATION</PDFText>
                <PDFText style={{ flex: 1, fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.white, textAlign: 'right' }}>Bill Amt.</PDFText>
                <PDFText style={{ flex: 1, fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.white, textAlign: 'right' }}>Discount</PDFText>
                <PDFText style={{ flex: 1, fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.white, textAlign: 'right' }}>Net Amt.</PDFText>
              </PDFView>
              {[
                ['OPD Consultancy', opdTotal],
                ['OPD Billing',     opdBillTotal],
                ['IPD Billing',     ipdTotal],
              ].map(([label, t], i) => (
                <PDFView key={label} style={{ flexDirection: 'row', paddingVertical: 3, paddingHorizontal: 8, backgroundColor: i % 2 === 0 ? C.white : C.altRow, borderBottomWidth: 0.3, borderBottomColor: C.border }}>
                  <PDFText style={{ flex: 2, fontSize: 7.5 }}>{label}</PDFText>
                  <PDFText style={{ flex: 1, fontSize: 7.5, textAlign: 'right' }}>{fmt(t.bill)}</PDFText>
                  <PDFText style={{ flex: 1, fontSize: 7.5, textAlign: 'right', color: C.red }}>{t.disc > 0 ? `(-) ${fmt(t.disc)}` : fmt(0)}</PDFText>
                  <PDFText style={{ flex: 1, fontSize: 7.5, fontFamily: 'Roboto', fontWeight: 700, textAlign: 'right' }}>{fmt(t.net)}</PDFText>
                </PDFView>
              ))}
              <PDFView style={{ flexDirection: 'row', paddingVertical: 5, paddingHorizontal: 8, backgroundColor: C.primaryDark, alignItems: 'center' }}>
                <PDFText style={{ flex: 2, fontSize: 9, fontFamily: 'Roboto', fontWeight: 700, color: C.white }}>GRAND TOTAL (NET)</PDFText>
                <PDFText style={{ flex: 1, fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: C.white, textAlign: 'right' }}>{fmt(grandBill)}</PDFText>
                <PDFText style={{ flex: 1, fontSize: 8, fontFamily: 'Roboto', fontWeight: 700, color: '#FF8A80', textAlign: 'right' }}>(-) {fmt(grandDisc)}</PDFText>
                <PDFText style={{ flex: 1, fontSize: 10, fontFamily: 'Roboto', fontWeight: 700, color: '#FFD54F', textAlign: 'right' }}>{fmtINR(grandNet)}</PDFText>
              </PDFView>
            </PDFView>
            <PDFText style={{ fontSize: 6, color: C.gray, marginTop: 2 }}>
              * Discount includes both service-item level discount and bill-level discount (bill-level counted once per bill).
            </PDFText>

            {/* FOOTER */}
            <PDFView style={styles.footer} fixed>
              <PDFText style={styles.footerLeft}>Doctor Ledger Detail — {doctorName}  |  Printed: {printTime}</PDFText>
              <PDFText style={styles.footerRight} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
            </PDFView>

          </PDFPage>
        </Document>
      </PDFViewer>
    </div>
  );
};

export default DoctorLedgerReportPdf;
